/**
 * TypeScript type definitions for the Daily Facts system
 * Used throughout the application for type safety and consistency
 */

// Daily Fact categories for content
export type FactCategory = 
  | 'General'
  | 'Historical' 
  | 'People'
  | 'Events'
  | 'Traditions'
  | 'Arts'
  | 'Music'
  | 'Food'
  | 'Community'
  | 'Heritage';

// Main daily fact interface
export interface DailyFact {
  id: number;
  title: string;
  fact_text: string;
  category: FactCategory;
  source?: string;
  source_url?: string;
  image_url?: string;
  featured: boolean;
  active: boolean;
  display_date?: string; // ISO date string
  priority: number;
  tags: string[];
  created_at: string;
  updated_at: string;
}

// Form values for creating/editing facts
export interface DailyFactFormValues {
  title: string;
  fact_text: string;
  category: FactCategory;
  source?: string;
  source_url?: string;
  image_url?: string;
  featured?: boolean;
  active?: boolean;
  display_date?: string;
  priority?: number;
  tags?: string[];
}

// Options for fetching facts
export interface GetFactsOptions {
  isAdmin?: boolean;
  category?: FactCategory;
  featured?: boolean;
  active?: boolean;
  limit?: number;
  offset?: number;
  search?: string;
  tags?: string[];
}

// Daily fact display configuration
export interface DailyFactDisplayOptions {
  showSource?: boolean;
  showCategory?: boolean;
  showImage?: boolean;
  maxLength?: number;
  className?: string;
}

// Fact statistics for admin dashboard
export interface FactsStats {
  total: number;
  active: number;
  featured: number;
  byCategory: Record<FactCategory, number>;
  recentlyAdded: number;
}

// Search/filter interface for facts management
export interface FactsFilter {
  category?: FactCategory | 'All';
  featured?: boolean | 'All';
  active?: boolean | 'All';
  search?: string;
  tags?: string[];
  dateFrom?: string;
  dateTo?: string;
}

// Fact validation result
export interface FactValidationResult {
  valid: boolean;
  errors: {
    title?: string;
    fact_text?: string;
    category?: string;
    source_url?: string;
    display_date?: string;
    priority?: string;
  };
}

// Service response types
export interface CreateFactResponse {
  fact: DailyFact;
  success: boolean;
  message: string;
}

export interface UpdateFactResponse {
  fact: DailyFact;
  success: boolean;
  message: string;
}

export interface DeleteFactResponse {
  success: boolean;
  message: string;
}

// Fact display component props
export interface DailyFactCardProps {
  fact?: DailyFact;
  loading?: boolean;
  options?: DailyFactDisplayOptions;
  onRefresh?: () => void;
  className?: string;
}

// Admin management component props
export interface FactsManagementProps {
  initialFilter?: FactsFilter;
}

// Constants for the facts system
export const FACT_CATEGORIES: FactCategory[] = [
  'General',
  'Historical',
  'People', 
  'Events',
  'Traditions',
  'Arts',
  'Music',
  'Food',
  'Community',
  'Heritage'
];

export const FACT_CATEGORY_COLORS: Record<FactCategory, string> = {
  General: 'bg-purple-100 text-purple-800',
  Historical: 'bg-amber-100 text-amber-800',
  People: 'bg-blue-100 text-blue-800',
  Events: 'bg-green-100 text-green-800',
  Traditions: 'bg-red-100 text-red-800',
  Arts: 'bg-pink-100 text-pink-800',
  Music: 'bg-indigo-100 text-indigo-800',
  Food: 'bg-orange-100 text-orange-800',
  Community: 'bg-teal-100 text-teal-800',
  Heritage: 'bg-yellow-100 text-yellow-800'
};

export const FACT_CATEGORY_DESCRIPTIONS: Record<FactCategory, string> = {
  General: 'General information and practices',
  Historical: 'Historical events and milestones',
  People: 'Notable individuals and their contributions',
  Events: 'Significant events',
  Traditions: 'Traditions and customs',
  Arts: 'Visual arts, crafts, and artistic expressions',
  Music: 'Musical traditions and genres',
  Food: 'Culinary traditions and food culture',
  Community: 'Community practices and social aspects',
  Heritage: 'Heritage and preservation'
};

// Default fact for loading states
export const DEFAULT_FACT: DailyFact = {
  id: 0,
  title: 'Loading Fact...',
  fact_text: 'Discovering interesting information...',
  category: 'General',
  featured: false,
  active: true,
  priority: 0,
  tags: [],
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
};

// Validation constants
export const FACT_VALIDATION = {
  TITLE_MIN_LENGTH: 10,
  TITLE_MAX_LENGTH: 200,
  FACT_TEXT_MIN_LENGTH: 50,
  FACT_TEXT_MAX_LENGTH: 1000,
  MAX_TAGS: 10,
  MIN_PRIORITY: 0,
  MAX_PRIORITY: 10
} as const;






